import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User, CompanyMembershipInfo } from "@ridereserv/shared/types";
import { PLATFORM_APPROVAL_STATUS } from "@ridereserv/shared/constants";

interface AuthState {
  user: User | null;
  memberships: CompanyMembershipInfo[];
  isAuthenticated: boolean;
  isLoading: boolean;
  selectedBusinessId: string | null;

  // Actions
  setAuth: (user: User, memberships: CompanyMembershipInfo[]) => void;
  clearAuth: () => void;
  setLoading: (isLoading: boolean) => void;
  setSelectedBusinessId: (businessId: string | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      memberships: [],
      isAuthenticated: false,
      isLoading: true,
      selectedBusinessId: null,

      // Set auth state - tokens are managed by Supabase, not stored here
      setAuth: (user, memberships) => {
        set({
          user,
          memberships,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      clearAuth: () => {
        set({
          user: null,
          memberships: [],
          isAuthenticated: false,
          isLoading: false,
          selectedBusinessId: null,
        });
      },

      setLoading: (isLoading) => { set({ isLoading }); },

      setSelectedBusinessId: (businessId) => { set({ selectedBusinessId: businessId }); },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        memberships: state.memberships,
        isAuthenticated: state.isAuthenticated,
        selectedBusinessId: state.selectedBusinessId,
      }),
    }
  )
);

// Helper selectors for driver approval status
export const useIsPendingDriver = () => {
  const user = useAuthStore((state) => state.user);
  return user?.driverApprovalStatus === PLATFORM_APPROVAL_STATUS.PENDING;
};

export const useIsApprovedDriver = () => {
  const user = useAuthStore((state) => state.user);
  return user?.driverApprovalStatus === PLATFORM_APPROVAL_STATUS.APPROVED;
};

export const useIsRejectedDriver = () => {
  const user = useAuthStore((state) => state.user);
  return user?.driverApprovalStatus === PLATFORM_APPROVAL_STATUS.REJECTED;
};

export const useHasDriverMembership = () => {
  const memberships = useAuthStore((state) => state.memberships);
  return memberships.some((m) => m.roles.includes("driver"));
};

// Helper selectors for business admin approval status
export const useIsPendingBusinessAdmin = () => {
  const user = useAuthStore((state) => state.user);
  return user?.businessApprovalStatus === PLATFORM_APPROVAL_STATUS.PENDING;
};

export const useIsApprovedBusinessAdmin = () => {
  const user = useAuthStore((state) => state.user);
  return user?.businessApprovalStatus === PLATFORM_APPROVAL_STATUS.APPROVED;
};

export const useIsRejectedBusinessAdmin = () => {
  const user = useAuthStore((state) => state.user);
  return user?.businessApprovalStatus === PLATFORM_APPROVAL_STATUS.REJECTED;
};

export const useHasAdminMembership = () => {
  const memberships = useAuthStore((state) => state.memberships);
  return memberships.some((m) => m.roles.includes("admin"));
};

// Helper selectors for driver subscription tier
export const useDriverSubscriptionTier = () => {
  const memberships = useAuthStore((state) => state.memberships);
  const driverMembership = memberships.find((m) => m.roles.includes("driver"));
  return driverMembership?.subscriptionTierId ?? null;
};

export const useIsFreeDriver = () => {
  const memberships = useAuthStore((state) => state.memberships);
  return memberships.some(
    (m) => m.roles.includes("driver") && m.subscriptionTierId === "free_driver"
  );
};

export const useIsPaidDriver = () => {
  const memberships = useAuthStore((state) => state.memberships);
  return memberships.some(
    (m) => m.roles.includes("driver") && m.subscriptionTierId === "paid_driver"
  );
};

// Get solo driver membership (free_driver or paid_driver tier)
export const useSoloDriverMembership = () => {
  const memberships = useAuthStore((state) => state.memberships);
  return memberships.find(
    (m) => m.subscriptionTierId === "free_driver" || m.subscriptionTierId === "paid_driver"
  );
};

// Get business company memberships where user is admin/manager
export const useBusinessMemberships = () => {
  const memberships = useAuthStore((state) => state.memberships);
  return memberships.filter(
    (m) =>
      m.roles.some((r) => ["admin", "manager"].includes(r)) &&
      !["free_driver", "paid_driver"].includes(m.subscriptionTierId ?? "")
  );
};

// Get the currently selected business (for sidebar navigation)
// Falls back to first business membership if none selected or invalid
export const useSelectedBusiness = () => {
  const selectedBusinessId = useAuthStore((state) => state.selectedBusinessId);
  const businessMemberships = useBusinessMemberships();

  // If a business is selected and valid, return it
  if (selectedBusinessId) {
    const selected = businessMemberships.find((m) => m.companyId === selectedBusinessId);
    if (selected) return selected;
  }

  // Fallback to first business membership
  return businessMemberships[0] ?? null;
};
